import type { QueryClient } from "@tanstack/react-query"
import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
} from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { TanStackDevtools } from "@tanstack/react-devtools"

import appCss from "../styles.css?url"

type RouterContext = {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Groundline · Golf booking and operations",
      },
      {
        name: "description",
        content:
          "Find a tee time, book without an account, and manage the same live schedule from the course desk.",
      },
      {
        name: "application-name",
        content: "Groundline",
      },
      {
        name: "apple-mobile-web-app-title",
        content: "Groundline",
      },
      {
        name: "apple-mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "format-detection",
        content: "telephone=no",
      },
      {
        name: "theme-color",
        content: "#0D1B24",
      },
      {
        property: "og:title",
        content: "Groundline · Plot the round from booking to course",
      },
      {
        property: "og:description",
        content:
          "Book a round without an account, then run the same live schedule from the course desk.",
      },
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:site_name",
        content: "Groundline",
      },
      {
        property: "og:url",
        content: "https://golf-booking.jericop.dev",
      },
      {
        property: "og:image",
        content: "https://golf-booking.jericop.dev/og.png",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        property: "og:image:alt",
        content: "Groundline golf booking and course operations preview",
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: "Groundline · Plot the round from booking to course",
      },
      {
        name: "twitter:description",
        content:
          "Book a round without an account, then run the same live schedule from the course desk.",
      },
      {
        name: "twitter:image",
        content: "https://golf-booking.jericop.dev/og.png",
      },
      {
        name: "twitter:image:alt",
        content: "Groundline golf booking and course operations preview",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "manifest",
        href: "/manifest.json",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32.png",
      },
      {
        rel: "alternate icon",
        type: "image/x-icon",
        href: "/favicon.ico",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "mask-icon",
        href: "/safari-pinned-tab.svg",
        color: "#0D1B24",
      },
      {
        rel: "canonical",
        href: "https://golf-booking.jericop.dev",
      },
    ],
  }),
  notFoundComponent: () => (
    <main className="site-frame grid min-h-svh place-items-center py-16 text-center">
      <div>
        <span className="eyebrow">Lost ball</span>
        <h1 className="page-title">That page is out of bounds.</h1>
        <p className="page-lede mx-auto">
          The route could not be found. Head back to the live tee sheet and
          choose another time.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex min-h-11 items-center rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground"
        >
          Return to tee times
        </a>
      </div>
    </main>
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <footer className="portfolio-footer">
          <span>
            Groundline is a portfolio demo. Courses, people, bookings, and
            transactions are synthetic; staff changes persist in a shared demo
            database.
          </span>
          <a href="https://jericop.dev" rel="author">
            Built by Jerico Pulvera
          </a>
        </footer>
        {import.meta.env.DEV ? (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        ) : null}
        <Scripts />
      </body>
    </html>
  )
}
